import './css/main.css'
import { useNavigate } from "react-router-dom"
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { SyncLoader } from "react-spinners";

function SoundManage() {
    const dark = useSelector((state) => state.Darkmode.value);
    const darkname = dark ? "dark" : "";

    // ================== API PATHS ==================
    const API = {
        sound: {
            get: "/api/sound",
            create: "/api/sound",          // 신규 등록
            update: "/api/sound/update",   // 수정
            delete: "/api/sound/delete",   // 삭제
            idKey: "soundId",
        },
    };

    // ================== STATE ==================
    // 사운드 목록 관련 상태 추가
    const [soundList, setSoundList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // 각 사운드별 파일 상태 관리
    const [soundFiles, setSoundFiles] = useState({});
    const [soundUrls, setSoundUrls] = useState({});

    const [reload, setReload] = useState(false);

    useEffect(() => {
        loadSound();
        return () => {
            // 모든 blob URL 정리
            Object.values(soundUrls).forEach(url => {
                if (url && url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload]);

    // ================== LOADERS ==================
    const loadSound = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios.get(process.env.REACT_APP_API + API.sound.get);
            if (data?.code === 1000) {
                const soundList = data?.data?.sound || [];
                setSoundList(soundList);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    // ================== COMMON HANDLERS ==================
    // 사운드별 파일 변경 핸들러
    const handleSoundFileChange = (soundId, file) => {
        if (!file) return;
        
        // 기존 blob URL 정리
        if (soundUrls[soundId] && soundUrls[soundId].startsWith("blob:")) {
            URL.revokeObjectURL(soundUrls[soundId]);
        }
        
        // 새로운 blob URL 생성
        const objectUrl = URL.createObjectURL(file);
        
        setSoundFiles(prev => ({
            ...prev,
            [soundId]: file
        }));
        
        setSoundUrls(prev => ({
            ...prev,
            [soundId]: objectUrl
        }));
    };

    // 사운드별 파일 취소 핸들러
    const handleSoundFileCancel = (soundId, originalUrl) => {
        // blob URL 정리
        if (soundUrls[soundId] && soundUrls[soundId].startsWith("blob:")) {
            URL.revokeObjectURL(soundUrls[soundId]);
        }
        
        setSoundFiles(prev => {
            const newState = { ...prev };
            delete newState[soundId];
            return newState;
        });
        
        setSoundUrls(prev => {
            const newState = { ...prev };
            delete newState[soundId];
            return newState;
        });
    };

    // create / update / delete
    const createMedia = async (type, file) => {
        if (!file) return alert("오디오 파일을 선택하세요.");
        const spec = API[type];
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { data } = await axios.post(process.env.REACT_APP_API + spec.create, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (data?.code < 2000) setReload((v) => !v);
            else alert("등록 실패");
        } catch (e) {
            console.error(e);
            alert("등록 중 오류가 발생했습니다.");
        }
    };

    const updateMedia = async (type, id, file) => {
        if (!file) return alert("오디오 파일을 선택하세요.");
        const spec = API[type];
        const formData = new FormData();
        formData.append(spec.idKey || "id", id ?? "");
        formData.append("status", "1");
        formData.append("file", file);
        try {
            const { data } = await axios.post(process.env.REACT_APP_API + spec.update, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (data?.code < 2000) setReload((v) => !v);
            else alert("수정 실패");
        } catch (e) {
            console.error(e);
            alert("수정 중 오류가 발생했습니다.");
        }
    };

    const deleteMedia = async (type, id) => {
        const spec = API[type];
        if (!window.confirm("정말로 삭제하시겠습니까?")) return;
        try {
            const { data } = await axios.post(process.env.REACT_APP_API + spec.delete, {
                [spec.idKey || "id"]: id,
            });
            if (data?.code < 2000) setReload((v) => !v);
            else alert("삭제 실패");
        } catch (e) {
            console.error(e);
            alert("삭제 중 오류가 발생했습니다.");
        }
    };

    // ================== UI COMPONENT ==================
    // 사운드 목록 컴포넌트
    const SoundList = ({ 
        sounds, 
        onCreateMedia,
        onUpdateMedia,
        soundFiles,
        soundUrls,
        onSoundFileChange,
        onSoundFileCancel,
    }) => {
        if (sounds.length === 0) {
            return (
                <div style={{
                    textAlign: "center",
                    padding: "40px 16px",
                    color: dark ? "#a0a0a0" : "#666666"
                }}>
                    <div style={{
                        fontSize: "48px",
                        marginBottom: "16px",
                        opacity: 0.3
                    }}>🎵</div>
                    <p style={{ fontSize: "16px", margin: 0, fontWeight: "500", marginBottom: "6px" }}>등록된 사운드가 없습니다</p>
                    <p style={{ fontSize: "13px", margin: 0, opacity: 0.7 }}>새로운 사운드 파일을 추가해주세요</p>
                </div>
            );
        }

        return (
            <div>
                <div style={{
                    padding: "20px 20px 16px 20px",
                    borderBottom: dark ? "1px solid #333" : "1px solid #e0e0e0",
                    backgroundColor: dark ? "#222222" : "#f8f9fa"
                }}>
                    <div style={{
                        display: "flex",
                        gap:"10px",
                        alignItems: "center",
                        marginBottom: "12px"
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "600",
                            color: dark ? "#ffffff" : "#1a1a1a"
                        }}>사운드 관리</h3>
                        <span style={{
                            backgroundColor: dark ? "#404040" : "#e0e0e0",
                            color: dark ? "#e0e0e0" : "#666666",
                            padding: "4px 10px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "500"
                        }}>총 {sounds.length}개</span>
                    </div>
                </div>
                <div style={{ 
                    maxHeight: "none", 
                    overflowY: "visible",
                    padding: "16px"
                }}>
                    <div style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "start",
                        justifyItems: "start"
                    }}>
                        {sounds.map((sound) => {
                            const hasOrigin = !!sound.sound_url;
                            const hasTemp = !!soundFiles[sound.id];
                            const isModified = hasOrigin && hasTemp;
                            const isNew = !hasOrigin && hasTemp;
                            const currentFile = soundFiles[sound.id];
                            const currentUrl = soundUrls[sound.id] || sound.sound_url;
                            
                            return (
                                <div 
                                    key={sound.id} 
                                    style={{
                                        padding: "16px",
                                        borderRadius: "8px",
                                        transition: "all 0.2s ease",
                                        backgroundColor: dark ? "#222222" : "#ffffff",
                                        border: dark ? "1px solid #333" : "1px solid #e0e0e0",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                        height: "fit-content",
                                        width: "300px"
                                    }}
                                >
                                    {/* 사운드 기본 정보 */}
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        marginBottom: "16px"
                                    }}>
                                        <div style={{ flex: "1" }}>
                                            <div style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: dark ? "#ffffff" : "#1a1a1a",
                                                marginBottom: "8px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}>{sound.sound_nm || `사운드 ${sound.id}`}</div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: dark ? "#a0a0a0" : "#666666",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px"
                                            }}>
                                                <span>📅 {sound.mod_dt ? new Date(sound.mod_dt).toLocaleDateString() : '날짜 없음'}</span>
                                            </div>
                                            {sound.sound_tooltip && (
                                                <div style={{
                                                    fontSize: "12px",
                                                    color: dark ? "#e0e0e0" : "#333333",
                                                    marginTop: "8px",
                                                    padding: "8px 12px",
                                                    backgroundColor: dark ? "#2a4a6a" : "#e3f2fd",
                                                    borderRadius: "6px",
                                                    border: dark ? "1px solid #4a90e2" : "1px solid #1976d2",
                                                    lineHeight: "1.4",
                                                    boxShadow: dark ? "0 1px 3px rgba(74, 144, 226, 0.2)" : "0 1px 3px rgba(25, 118, 210, 0.15)"
                                                }}>
                                                    💡 {sound.sound_tooltip}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 모든 사운드의 편집 영역 (항상 표시) */}
                                    <div style={{
                                        borderTop: dark ? "1px solid #333" : "1px solid #e0e0e0",
                                        paddingTop: "16px"
                                    }}>
                                        {/* 오디오 플레이어 */}
                                        <div style={{
                                            marginBottom: "16px"
                                        }}>
                                            <audio 
                                                src={currentUrl} 
                                                controls 
                                                style={{ 
                                                    width: "100%",
                                                    height: "40px",
                                                    borderRadius: "4px"
                                                }} 
                                            />
                                        </div>

                                        {/* 파일 업로드/교체 영역 */}
                                        <div style={{
                                            width: "100%",
                                            height: "80px",
                                            border: "2px dashed #ccc",
                                            borderRadius: "6px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            position: "relative",
                                            backgroundColor: dark ? "#222222" : "#f8f9fa",
                                            marginBottom: "16px"
                                        }}>
                                            <div style={{ 
                                                color: dark ? "#a0a0a0" : "#666666", 
                                                textAlign: "center",
                                                fontSize: "12px"
                                            }}>
                                                <div style={{ fontSize: "24px", marginBottom: "4px", opacity: 0.5 }}>🎵</div>
                                                <div style={{ fontWeight: "500", marginBottom: "2px" }}>파일을 드래그하거나 클릭하여 교체하세요</div>
                                                <div style={{ fontSize: "10px", opacity: 0.7 }}>지원 형식: MP3, WAV, OGG 등</div>
                                            </div>
                                            <input
                                                type="file"
                                                accept="audio/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        onSoundFileChange(sound.id, file);
                                                    }
                                                }}
                                                data-sound-id={sound.id}
                                                style={{
                                                    position: "absolute",
                                                    top: 0, left: 0, width: "100%", height: "100%",
                                                    opacity: 0, cursor: "pointer",
                                                }}
                                            />
                                        </div>

                                        {/* 버튼 영역 */}
                                        <div style={{ 
                                            display: "flex", 
                                            gap: "6px", 
                                            flexWrap: "wrap",
                                            justifyContent: "center"
                                        }}>
                                            {/* 파일 교체 버튼 */}
                                            <button
                                                style={{
                                                    backgroundColor: "#f57c00",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "6px 12px",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    fontWeight: "500",
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease"
                                                }}
                                                onClick={() => {
                                                    const fileInput = document.querySelector(`input[type="file"][data-sound-id="${sound.id}"]`);
                                                    if (fileInput) fileInput.click();
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#ef6c00";
                                                    e.currentTarget.style.transform = "translateY(-1px)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#f57c00";
                                                    e.currentTarget.style.transform = "none";
                                                }}
                                            >
                                                🔄 파일 교체
                                            </button>

                                            {/* 수정/등록 버튼 */}
                                            {(isModified || isNew) && (
                                                <button
                                                    style={{
                                                        backgroundColor: "#1976d2",
                                                        color: "white",
                                                        border: "none",
                                                        padding: "6px 12px",
                                                        borderRadius: "4px",
                                                        fontSize: "11px",
                                                        fontWeight: "500",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease",
                                                        boxShadow: "0 1px 3px rgba(25, 118, 210, 0.3)"
                                                    }}
                                                    onClick={() => {
                                                        if (isNew) {
                                                            onCreateMedia("sound", currentFile);
                                                        } else if (isModified) {
                                                            onUpdateMedia("sound", sound.id, currentFile);
                                                        }
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#1565c0";
                                                        e.currentTarget.style.transform = "translateY(-1px)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#1976d2";
                                                        e.currentTarget.style.transform = "none";
                                                    }}
                                                >
                                                    {isNew ? "✅ 등록하기" : "💾 수정하기"}
                                                </button>
                                            )}

                                            {/* 취소 버튼 */}
                                            {(isModified || isNew) && (
                                                <button
                                                    style={{
                                                        backgroundColor: "#757575",
                                                        color: "white",
                                                        border: "none",
                                                        padding: "6px 12px",
                                                        borderRadius: "4px",
                                                        fontSize: "11px",
                                                        fontWeight: "500",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease"
                                                    }}
                                                    onClick={() => onSoundFileCancel(sound.id, sound.sound_url)}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#616161";
                                                        e.currentTarget.style.transform = "translateY(-1px)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#757575";
                                                        e.currentTarget.style.transform = "none";
                                                    }}
                                                >
                                                    ❌ 취소
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // ================== RENDER ==================
    return (
        <div className="main">
            <div className={`main-box ${darkname}`} style={{ 
                minHeight: "calc(100vh - 200px)"
            }}>
                {/* 통합된 사운드 관리 패널 */}
                <div style={{ 
                    backgroundColor: dark ? "#1a1a1a" : "#ffffff",
                    borderRadius: "12px",
                    border: dark ? "1px solid #333" : "1px solid #e0e0e0",
                    boxShadow: dark ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.08)",
                    maxWidth: "1200px"
                }}>
                    {isLoading ? (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "200px",
                            color: dark ? "#e0e0e0" : "#666666"
                        }}>
                            <SyncLoader color="#1976d2" size={12} />
                            <p style={{ marginTop: "16px", fontSize: "13px", fontWeight: "500" }}>사운드 목록을 불러오는 중...</p>
                        </div>
                    ) : (
                        <SoundList 
                            sounds={soundList}
                            onCreateMedia={createMedia}
                            onUpdateMedia={updateMedia}
                            soundFiles={soundFiles}
                            soundUrls={soundUrls}
                            onSoundFileChange={handleSoundFileChange}
                            onSoundFileCancel={handleSoundFileCancel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default SoundManage;